import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { FiDownload, FiPrinter, FiRotateCcw, FiShield } from 'react-icons/fi';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import * as XLSX from 'xlsx';
import { db } from '../../config/firebase';
import { useAuth } from '../../context/AuthContext';
import PageLayout from '../../components/PageLayout';
import { colors, spacing, borderRadius, shadows, typography } from '../../theme/colors';
import useCurrency from '../../hooks/useCurrency';
import { formatAmount } from '../../utils/documentNumbering';

const POSTED_STATUSES = ['Posted', 'Paid'];
const monthLabel = (m) => {
  if (!m) return '';
  const [y, mn] = m.split('-');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[parseInt(mn, 10) - 1]} ${y}`;
};

const StatutoryContributions = () => {
  const { activeCompany, activeFinancialYear, activeBranch } = useAuth();
  const { formatCurrency } = useCurrency();

  const [runs, setRuns] = useState([]);
  const [fnfs, setFnfs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [financialYears, setFinancialYears] = useState([]);

  const [selectedFYId, setSelectedFYId] = useState('');
  const [viewMode, setViewMode] = useState('byMonth'); // 'byMonth' | 'byStaff' | 'byDeduction'

  // ─── Load FYs + PAY + FNF ───
  const loadFYs = useCallback(async () => {
    if (!activeCompany?.id) return;
    const snap = await getDocs(query(
      collection(db, 'companies', activeCompany.id, 'financialYears'),
      orderBy('startDate', 'desc')
    ));
    const fys = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    setFinancialYears(fys);
    if (!selectedFYId) setSelectedFYId(activeFinancialYear?.id || fys[0]?.id || '');
  }, [activeCompany, activeFinancialYear, selectedFYId]);

  const loadData = useCallback(async () => {
    if (!activeCompany?.id || !selectedFYId) return;
    setLoading(true);
    try {
      const [paySnap, fnfSnap] = await Promise.all([
        getDocs(query(
          collection(db, 'companies', activeCompany.id, 'transactions'),
          where('tranTypeCode', '==', 'PAY'),
          where('financialYearId', '==', selectedFYId)
        )),
        getDocs(query(
          collection(db, 'companies', activeCompany.id, 'transactions'),
          where('tranTypeCode', '==', 'FNF'),
          where('financialYearId', '==', selectedFYId)
        ))
      ]);
      let payRuns = paySnap.docs.map(d => ({ id: d.id, ...d.data() }))
        .filter(t => POSTED_STATUSES.includes(t.status));
      let fnfRuns = fnfSnap.docs.map(d => ({ id: d.id, ...d.data() }))
        .filter(t => POSTED_STATUSES.includes(t.status));
      if (activeBranch && activeBranch.id !== 'all') {
        payRuns = payRuns.filter(t => t.branchId === activeBranch.id);
        fnfRuns = fnfRuns.filter(t => t.branchId === activeBranch.id);
      }
      setRuns(payRuns);
      setFnfs(fnfRuns);
    } catch (e) { console.error('Load error:', e); }
    finally { setLoading(false); }
  }, [activeCompany, selectedFYId, activeBranch]);

  useEffect(() => { loadFYs(); }, [loadFYs]);
  useEffect(() => { loadData(); }, [loadData]);

  // ─── Aggregate all deductions across all PAY + FNF ───
  // Each "contribution" = { deductionName, accountName, period (YYYY-MM), staffId, staffName, amount }
  const allContributions = useMemo(() => {
    const list = [];

    // From PAY runs
    runs.forEach(run => {
      (run.payrollLines || []).forEach(line => {
        (line.deductions || []).forEach(d => {
          const amt = Number(d.computed ?? d.value) || 0;
          if (amt <= 0) return;
          list.push({
            period: run.payrollMonth,
            source: 'Payroll',
            sourceDoc: run.docNumber,
            deductionName: d.name,
            accountName: d.accountName || d.accountCode || '—',
            accountCode: d.accountCode,
            staffId: line.staffId,
            staffCode: line.staffCode,
            staffName: line.staffName,
            department: line.department,
            amount: amt
          });
        });
      });
    });

    // From FNF settlements
    fnfs.forEach(fnf => {
      (fnf.fnfDeductions || []).forEach(d => {
        const amt = Number(d.amount) || 0;
        if (amt <= 0) return;
        list.push({
          period: (fnf.docDate || '').slice(0, 7),  // F&F doesn't have payrollMonth
          source: 'F&F',
          sourceDoc: fnf.docNumber,
          deductionName: d.name,
          accountName: d.accountName || d.accountCode || '—',
          accountCode: d.accountCode,
          staffId: fnf.partyId,
          staffCode: fnf.staffCode,
          staffName: fnf.partyName,
          department: fnf.department,
          amount: amt
        });
      });
    });

    return list;
  }, [runs, fnfs]);

  // ─── Pivots ───
  // By Month: { period: { [deductionName]: amount, total: ... } }
  const byMonthPivot = useMemo(() => {
    const periods = {};
    const allDeductions = new Set();
    allContributions.forEach(c => {
      allDeductions.add(c.deductionName);
      if (!periods[c.period]) periods[c.period] = { period: c.period, total: 0 };
      periods[c.period][c.deductionName] = (periods[c.period][c.deductionName] || 0) + c.amount;
      periods[c.period].total += c.amount;
    });
    const rows = Object.values(periods).sort((a, b) => (b.period || '').localeCompare(a.period || ''));
    return { rows, deductionNames: [...allDeductions].sort() };
  }, [allContributions]);

  // By Staff: { staffId: { ...staff, [deductionName]: amount, total } }
  const byStaffPivot = useMemo(() => {
    const staffMap = {};
    const allDeductions = new Set();
    allContributions.forEach(c => {
      allDeductions.add(c.deductionName);
      if (!c.staffId) return;
      if (!staffMap[c.staffId]) {
        staffMap[c.staffId] = {
          staffId: c.staffId, staffCode: c.staffCode, staffName: c.staffName, department: c.department, total: 0
        };
      }
      staffMap[c.staffId][c.deductionName] = (staffMap[c.staffId][c.deductionName] || 0) + c.amount;
      staffMap[c.staffId].total += c.amount;
    });
    const rows = Object.values(staffMap).sort((a, b) => b.total - a.total);
    return { rows, deductionNames: [...allDeductions].sort() };
  }, [allContributions]);

  // By Deduction: { deductionName: { account, total, periods: {month: amount} } }
  const byDeductionPivot = useMemo(() => {
    const map = {};
    allContributions.forEach(c => {
      const key = c.deductionName;
      if (!map[key]) {
        map[key] = { deductionName: key, accountName: c.accountName, accountCode: c.accountCode, total: 0, periods: {}, staffCount: new Set() };
      }
      map[key].total += c.amount;
      map[key].periods[c.period] = (map[key].periods[c.period] || 0) + c.amount;
      map[key].staffCount.add(c.staffId);
    });
    return Object.values(map)
      .map(d => ({ ...d, staffCount: d.staffCount.size }))
      .sort((a, b) => b.total - a.total);
  }, [allContributions]);

  const totalAcrossAll = useMemo(() =>
    allContributions.reduce((s, c) => s + c.amount, 0)
  , [allContributions]);

  // ─── Excel ───
  const exportToExcel = () => {
    const wb = XLSX.utils.book_new();

    // Sheet 1: Raw contributions
    const raw = allContributions.map(c => ({
      'Period': monthLabel(c.period),
      'Source': c.source,
      'Source Doc': c.sourceDoc,
      'Deduction': c.deductionName,
      'Account': c.accountName,
      'Staff Code': c.staffCode,
      'Staff Name': c.staffName,
      'Department': c.department,
      'Amount': c.amount
    }));
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(raw), 'All Contributions');

    // Sheet 2: By Month pivot
    const byMonthRows = byMonthPivot.rows.map(r => {
      const row = { Period: monthLabel(r.period) };
      byMonthPivot.deductionNames.forEach(d => { row[d] = r[d] || 0; });
      row.Total = r.total;
      return row;
    });
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(byMonthRows), 'By Month');

    XLSX.writeFile(wb, `StatutoryContributions_${selectedFYId}_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  return (
    <PageLayout pageTitle="Statutory Contributions" breadcrumb="Reports / Salary / Statutory Contributions">
      <FiltersCard>
        <FilterGrid>
          <FilterItem>
            <FilterLabel>Financial Year</FilterLabel>
            <FilterSelect value={selectedFYId} onChange={(e) => setSelectedFYId(e.target.value)}>
              {financialYears.map(fy => <option key={fy.id} value={fy.id}>{fy.yearName || fy.yearCode}</option>)}
            </FilterSelect>
          </FilterItem>
          <FilterItem>
            <FilterLabel>&nbsp;</FilterLabel>
            <ResetBtn onClick={() => setSelectedFYId(activeFinancialYear?.id || financialYears[0]?.id || '')}>
              <FiRotateCcw /> Reset
            </ResetBtn>
          </FilterItem>
        </FilterGrid>
      </FiltersCard>

      <KPIGrid>
        <KPI><KL>Periods Covered</KL><KV>{byMonthPivot.rows.length}</KV></KPI>
        <KPI><KL>Deduction Types</KL><KV>{byDeductionPivot.length}</KV></KPI>
        <KPI><KL>Staff Affected</KL><KV>{byStaffPivot.rows.length}</KV></KPI>
        <KPI $highlight><KL>Total Contributions</KL><KV>{formatCurrency(totalAcrossAll)}</KV></KPI>
      </KPIGrid>

      <ToolbarRow>
        <Tabs>
          <Tab $active={viewMode === 'byMonth'} onClick={() => setViewMode('byMonth')}>By Month</Tab>
          <Tab $active={viewMode === 'byDeduction'} onClick={() => setViewMode('byDeduction')}>By Deduction</Tab>
          <Tab $active={viewMode === 'byStaff'} onClick={() => setViewMode('byStaff')}>By Staff</Tab>
        </Tabs>
        <div style={{ display: 'flex', gap: spacing.sm }}>
          <ExportBtn onClick={exportToExcel}><FiDownload /> Excel</ExportBtn>
          <ExportBtn onClick={() => window.print()}><FiPrinter /> Print</ExportBtn>
        </div>
      </ToolbarRow>

      {loading ? <Loading>Loading...</Loading> : allContributions.length === 0 ? (
        <Empty>
          <EmptyIcon><FiShield /></EmptyIcon>
          <p>No statutory deductions found for this financial year. Run a payroll first.</p>
        </Empty>
      ) : (
        <TableCard>
          {viewMode === 'byMonth' && (
            <Table>
              <thead>
                <tr>
                  <Th>Period</Th>
                  {byMonthPivot.deductionNames.map(d => <Th key={d} style={{ textAlign: 'right' }}>{d}</Th>)}
                  <Th style={{ textAlign: 'right' }}>Total</Th>
                </tr>
              </thead>
              <tbody>
                {byMonthPivot.rows.map(r => (
                  <Tr key={r.period}>
                    <Td><MonthBadge>{monthLabel(r.period)}</MonthBadge></Td>
                    {byMonthPivot.deductionNames.map(d => (
                      <Td key={d} style={{ textAlign: 'right' }}>{r[d] ? formatCurrency(r[d]) : '—'}</Td>
                    ))}
                    <Td style={{ textAlign: 'right', fontWeight: 700, color: colors.primary.main }}>{formatCurrency(r.total)}</Td>
                  </Tr>
                ))}
              </tbody>
              <tfoot>
                <FootRow>
                  <td><strong>TOTAL</strong></td>
                  {byMonthPivot.deductionNames.map(d => {
                    const sum = byMonthPivot.rows.reduce((s, r) => s + (r[d] || 0), 0);
                    return <td key={d} style={{ textAlign: 'right', fontWeight: 700 }}>{formatCurrency(sum)}</td>;
                  })}
                  <td style={{ textAlign: 'right', fontWeight: 700, color: colors.primary.main }}>{formatCurrency(totalAcrossAll)}</td>
                </FootRow>
              </tfoot>
            </Table>
          )}

          {viewMode === 'byDeduction' && (
            <Table>
              <thead>
                <tr>
                  <Th>Deduction</Th>
                  <Th>Posting Account</Th>
                  <Th style={{ textAlign: 'center' }}># Staff</Th>
                  <Th style={{ textAlign: 'center' }}># Periods</Th>
                  <Th style={{ textAlign: 'right' }}>Total Amount</Th>
                  <Th style={{ textAlign: 'right' }}>Average / Period</Th>
                </tr>
              </thead>
              <tbody>
                {byDeductionPivot.map(d => (
                  <Tr key={d.deductionName}>
                    <Td><strong>{d.deductionName}</strong></Td>
                    <Td style={{ fontFamily: 'monospace', fontSize: 12 }}>{d.accountCode ? `${d.accountCode} — ${d.accountName}` : d.accountName}</Td>
                    <Td style={{ textAlign: 'center' }}>{d.staffCount}</Td>
                    <Td style={{ textAlign: 'center' }}>{Object.keys(d.periods).length}</Td>
                    <Td style={{ textAlign: 'right', fontWeight: 700, color: colors.primary.main }}>{formatCurrency(d.total)}</Td>
                    <Td style={{ textAlign: 'right' }}>{formatCurrency(d.total / Math.max(1, Object.keys(d.periods).length))}</Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          )}

          {viewMode === 'byStaff' && (
            <Table>
              <thead>
                <tr>
                  <Th>Staff</Th>
                  <Th>Department</Th>
                  {byStaffPivot.deductionNames.map(d => <Th key={d} style={{ textAlign: 'right' }}>{d}</Th>)}
                  <Th style={{ textAlign: 'right' }}>Total</Th>
                </tr>
              </thead>
              <tbody>
                {byStaffPivot.rows.map(r => (
                  <Tr key={r.staffId}>
                    <Td>
                      <strong>{r.staffName}</strong>
                      <Sub>{r.staffCode}</Sub>
                    </Td>
                    <Td>{r.department || '-'}</Td>
                    {byStaffPivot.deductionNames.map(d => (
                      <Td key={d} style={{ textAlign: 'right' }}>{r[d] ? formatCurrency(r[d]) : '—'}</Td>
                    ))}
                    <Td style={{ textAlign: 'right', fontWeight: 700, color: colors.primary.main }}>{formatCurrency(r.total)}</Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          )}
        </TableCard>
      )}
    </PageLayout>
  );
};

// ─── Styles ───
const FiltersCard = styled.div`background: ${colors.background.default}; border-radius: ${borderRadius.lg}; padding: ${spacing.md}; box-shadow: ${shadows.sm}; margin-bottom: ${spacing.md};`;
const FilterGrid = styled.div`display: flex; flex-wrap: wrap; gap: ${spacing.md}; align-items: flex-end;`;
const FilterItem = styled.div`display: flex; flex-direction: column; gap: 4px; min-width: 220px;`;
const FilterLabel = styled.label`font-size: 11px; font-weight: 600; color: ${colors.text.secondary}; text-transform: uppercase; letter-spacing: 0.5px;`;
const FilterSelect = styled.select`padding: ${spacing.sm}; border: 1px solid ${colors.border.light}; border-radius: ${borderRadius.sm}; font-size: ${typography.fontSize.sm};`;
const ResetBtn = styled.button`
  display: flex; align-items: center; gap: 4px;
  padding: ${spacing.sm} ${spacing.md};
  background: ${colors.neutral[100]}; border: 1px solid ${colors.border.light};
  border-radius: ${borderRadius.sm}; font-size: ${typography.fontSize.sm}; cursor: pointer;
`;
const KPIGrid = styled.div`display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: ${spacing.md}; margin-bottom: ${spacing.md};`;
const KPI = styled.div`
  background: ${props => props.$highlight ? colors.primary.main : colors.background.default};
  color: ${props => props.$highlight ? 'white' : colors.text.primary};
  padding: ${spacing.md}; border-radius: ${borderRadius.lg}; box-shadow: ${shadows.sm};
  border-left: 3px solid ${props => props.$highlight ? colors.primary.dark : colors.border.light};
`;
const KL = styled.div`font-size: 11px; opacity: 0.85; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 4px;`;
const KV = styled.div`font-size: 22px; font-weight: 700; font-family: monospace;`;

const ToolbarRow = styled.div`display: flex; justify-content: space-between; align-items: center; margin-bottom: ${spacing.md}; flex-wrap: wrap; gap: ${spacing.md};`;
const Tabs = styled.div`display: flex; gap: ${spacing.xs}; background: ${colors.neutral[100]}; border-radius: ${borderRadius.sm}; padding: 3px;`;
const Tab = styled.button`
  padding: ${spacing.sm} ${spacing.lg};
  background: ${props => props.$active ? colors.background.default : 'transparent'};
  color: ${props => props.$active ? colors.primary.main : colors.text.secondary};
  border: none; border-radius: ${borderRadius.sm};
  font-size: ${typography.fontSize.sm}; font-weight: ${props => props.$active ? 600 : 500};
  cursor: pointer; box-shadow: ${props => props.$active ? shadows.sm : 'none'};
`;
const ExportBtn = styled.button`
  display: flex; align-items: center; gap: 6px;
  padding: ${spacing.sm} ${spacing.md};
  background: ${colors.primary.main}; color: white; border: none;
  border-radius: ${borderRadius.sm}; cursor: pointer; font-weight: 600; font-size: ${typography.fontSize.sm};
`;
const Loading = styled.div`padding: ${spacing.xxl}; text-align: center; color: ${colors.text.secondary};`;
const Empty = styled.div`padding: ${spacing.xxl}; text-align: center; background: ${colors.background.default}; border-radius: ${borderRadius.lg}; display: flex; flex-direction: column; align-items: center; gap: ${spacing.md}; color: ${colors.text.secondary};`;
const EmptyIcon = styled.div`width: 80px; height: 80px; border-radius: 50%; background: ${colors.primary.main}15; color: ${colors.primary.main}; display: flex; align-items: center; justify-content: center; font-size: 36px;`;

const TableCard = styled.div`background: ${colors.background.default}; border-radius: ${borderRadius.lg}; box-shadow: ${shadows.sm}; overflow: auto;`;
const Table = styled.table`width: 100%; border-collapse: collapse;`;
const Th = styled.th`
  padding: ${spacing.md}; text-align: left; background: ${colors.neutral[50]}; color: ${colors.text.secondary};
  font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px;
  border-bottom: 1px solid ${colors.border.light}; white-space: nowrap;
`;
const Tr = styled.tr`&:hover { background: ${colors.neutral[50]}; }`;
const Td = styled.td`padding: ${spacing.sm} ${spacing.md}; font-size: ${typography.fontSize.sm}; border-bottom: 1px solid ${colors.neutral[100]};`;
const Sub = styled.div`font-size: 11px; color: ${colors.text.tertiary}; margin-top: 2px;`;
const MonthBadge = styled.span`
  display: inline-block; padding: 2px 8px;
  background: ${colors.primary.main}15; color: ${colors.primary.main};
  border-radius: ${borderRadius.xl}; font-size: 11px; font-weight: 700;
`;
const FootRow = styled.tr`
  background: ${colors.neutral[100]};
  td { padding: ${spacing.sm} ${spacing.md}; border-top: 2px solid ${colors.border.light}; font-size: ${typography.fontSize.sm}; }
`;

export default StatutoryContributions;
